import { ImagePlay } from "lucide-react"
import { useMemo, useState } from "react"
import { FlowReferenceAdder } from "@/components/flow/flow-reference-adder"
import { ImageLightboxDialog } from "@/components/image-lightbox-dialog"
import { ReferenceInputThumb } from "@/components/reference-inputs/reference-input-thumb"
import { SketchDialog } from "@/components/sketch/sketch-dialog"
import { useTranslation } from "@/hooks/use-translation"
import type { FlowAttachOutcome, FlowAttachRequest } from "@/lib/flow-attach-input"
import { REFERENCE_MEDIA_FILE_ACCEPT } from "@/lib/reference-file-policy"
import { cn } from "@/lib/utils"
import type { FlowNodeInput, FlowNodeRow } from "@/types/flow"

type ShotRefTile = {
  edgeId: number
  sourceNodeId: number
  mediaType: "image" | "video" | "audio"
  label: string | null
  keyframe: boolean
  position: number
  url: string | null
  thumbUrl: string | null
  downloadUrl: string | null
}

type FlowShotReferencesProps = {
  projectId: number
  frameFormat: string
  frameRate?: number
  node: FlowNodeRow
  canEdit: boolean
  onDisconnect: (edgeId: number) => void
  onMakeKeyframe: (sourceNodeId: number) => Promise<void>
  onAttach: (batch: FlowAttachRequest) => Promise<FlowAttachOutcome>
  className?: string
}

const KIND_RANK: Record<ShotRefTile["mediaType"], number> = {
  image: 0,
  video: 1,
  audio: 2,
}

function shotReferenceTiles(node: FlowNodeRow): ShotRefTile[] {
  const tiles = (node.inputs ?? []).flatMap((input) => tileFromInput(input))
  return tiles.sort((a, b) => {
    if (a.keyframe !== b.keyframe) return a.keyframe ? -1 : 1
    if (KIND_RANK[a.mediaType] !== KIND_RANK[b.mediaType]) return KIND_RANK[a.mediaType] - KIND_RANK[b.mediaType]
    return a.position - b.position || a.edgeId - b.edgeId
  })
}

function tileFromInput(input: FlowNodeInput): ShotRefTile[] {
  if ((input.target_port ?? "references") !== "links") return []
  const mediaType = input.media_type
  if (mediaType !== "image" && mediaType !== "video" && mediaType !== "audio") return []
  return [
    {
      edgeId: input.id,
      sourceNodeId: input.source_node_id,
      mediaType,
      label: input.reference_label ?? null,
      keyframe: Boolean(input.keyframe),
      position: input.reference_position ?? input.position,
      url: input.url ?? null,
      thumbUrl: input.thumb_url ?? null,
      downloadUrl: input.download_url ?? null,
    },
  ]
}

export function FlowShotReferences({
  projectId,
  frameFormat,
  frameRate,
  node,
  canEdit,
  onDisconnect,
  onMakeKeyframe,
  onAttach,
  className,
}: FlowShotReferencesProps) {
  const { t } = useTranslation(["shot", "flow", "common"])
  const tiles = useMemo(() => shotReferenceTiles(node), [node])
  const [preview, setPreview] = useState<ShotRefTile | null>(null)
  const [applyingId, setApplyingId] = useState<number | null>(null)
  const previewIsVideo = preview?.mediaType === "video"
  const previewTitle = preview?.keyframe
    ? t("tabs.storyboard")
    : preview?.label || t("referenceInputs.openPreview", { ns: "common" })

  const makeKeyframe = (tile: ShotRefTile) => {
    if (!canEdit || applyingId != null) return
    setApplyingId(tile.edgeId)
    void onMakeKeyframe(tile.sourceNodeId).finally(() => setApplyingId(null))
  }

  return (
    <div className={cn("min-h-0 flex-1 space-y-2 overflow-y-auto", className)}>
      <p className="text-[11px] font-medium tracking-wide text-ui-text-muted uppercase">{t("referenceInputs.title")}</p>
      {tiles.length === 0 && !canEdit ? (
        <p className="text-[11px] text-ui-text-muted">{t("column.referencesEmpty", { ns: "flow" })}</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {tiles.map((tile) => (
            <div key={tile.edgeId} className="flex flex-col gap-1">
              <ReferenceInputThumb
                url={tile.url}
                thumbUrl={tile.thumbUrl}
                mediaType={tile.mediaType}
                onOpenPreview={() => setPreview(tile)}
                onRemove={canEdit ? () => onDisconnect(tile.edgeId) : undefined}
                onApply={
                  canEdit && tile.mediaType === "image" && !tile.keyframe && applyingId == null
                    ? () => makeKeyframe(tile)
                    : undefined
                }
                applyIcon={ImagePlay}
                openPreviewTitle={
                  tile.keyframe ? t("tabs.storyboard") : t("referenceInputs.openPreview", { ns: "common" })
                }
                removeAriaLabel={t("referenceInputs.removeReference", { ns: "common" })}
                applyAriaLabel={t("edges.makeKeyframe", { ns: "flow" })}
              />
              {tile.keyframe ? (
                <span className="max-w-[5.5rem] truncate text-center text-[10px] text-ui-text-muted">
                  {t("tabs.storyboard")}
                </span>
              ) : tile.label ? (
                <span className="max-w-[5.5rem] truncate rounded bg-ui-surface-raised px-1 py-0.5 text-center font-mono text-[10px] text-ui-accent-text">
                  {tile.label}
                </span>
              ) : null}
            </div>
          ))}
          {canEdit ? (
            <FlowReferenceAdder
              projectId={projectId}
              frameFormat={frameFormat}
              frameRate={frameRate}
              initialShotId={node.shot_id}
              accepts={["image", "video", "audio"]}
              fileAccept={REFERENCE_MEDIA_FILE_ACCEPT}
              onAttach={onAttach}
            />
          ) : null}
        </div>
      )}
      <SketchDialog
        open={preview != null && !previewIsVideo}
        onOpenChange={(open) => {
          if (!open) setPreview(null)
        }}
        projectId={projectId}
        sourceUrl={preview?.mediaType === "image" ? (preview.downloadUrl ?? preview.url) : null}
        audioUrl={preview?.mediaType === "audio" ? preview.url : null}
        presentation="view"
        allowEdit={false}
        persist="none"
        headerTitle={previewTitle}
      />
      <ImageLightboxDialog
        open={preview != null && previewIsVideo}
        onOpenChange={(open) => {
          if (!open) setPreview(null)
        }}
        url={null}
        videoUrl={previewIsVideo ? preview?.url : null}
        title={previewTitle}
      />
    </div>
  )
}
